import { AuditState, AuditRecordDto } from '@lmt/shared-types';
import i18next from 'i18next';
import { isEmpty, isEqual } from 'lodash';
import { NS_DataBlockFields } from '../../helpers/localization/useScopedTranslation';

const customFields = 'Values';
export const emptyString = "''";

const dataBlockFieldNs = NS_DataBlockFields + ':';
const dataBlockAuditTables = new Set([
  'DataBlockDictionaryItem',
  'DataBlock',
  'Case',
  'Visit',
  'Patient',
]);

const localizationPrefixes: Record<string, Record<string, string>> = {
  User: {
    Roles: 'roles.list.default_roles.',
  },
  Doctor: {
    TitleName: dataBlockFieldNs,
  },
  DataBlockDictionaryColumn: {
    DataBlockDictionaryTitle: dataBlockFieldNs,
    ReferenceDictionaryIdTitle: dataBlockFieldNs,
  },
  DataBlockDictionaryItem: {
    DataBlockDictionaryTitle: dataBlockFieldNs,
    ReferenceDictionaryIdTitle: dataBlockFieldNs,
  },
  Visit: { Type: '', Status: '' },
};

export function convertAuditRecord(record: AuditRecordDto) {
  const state = record.state;
  const oldValues = flattenCustomFields(record.oldValues || {});
  const newValues = flattenCustomFields(record.newValues || {});
  const options = { tableName: record.tableName };
  let changes: { [key: string]: string } = {};
  switch (state) {
    case AuditState.Added: {
      Object.keys(newValues).forEach((field) => {
        changes[field] = valueToString(newValues[field], field, options);
      });
      break;
    }

    case AuditState.Deleted: {
      Object.keys(oldValues).forEach((field) => {
        changes[field] = valueToString(oldValues[field], field, options);
      });
      break;
    }

    case AuditState.Modified: {
      const allFields = new Set([
        ...Object.keys(oldValues),
        ...Object.keys(newValues),
      ]);
      allFields.forEach((field) => {
        const oldValue = valueToString(oldValues[field], field, options);
        const newValue = valueToString(newValues[field], field, options);

        if (isEmpty(field)) {
          changes[''] = newValue;
        } else if (!isEqual(oldValue, newValue)) {
          if (oldValue === emptyString) {
            changes[field] = `added "${newValue}"`;
          } else if (newValue === emptyString) {
            changes[field] = `deleted "${oldValue}"`;
          } else {
            changes[field] = `${oldValue} -> ${newValue}`;
          }
        }
      });
      break;
    }
    default: {
      break;
    }
  }

  changes = filterEmptyValues(changes);

  return {
    id: record.id,
    dateTime: record.dateTime,
    rowKey: i18next.t(record.rowKey as any),
    tableName: record.tableName,
    patientId: record.patientId,
    patientName: record.patientName,
    state: record.state,
    userId: record.userId,
    userName: record.userName,
    changes,
  };
}

function valueToString(
  value: any,
  fieldName?: string,
  options?: { tableName: string },
) {
  let localizationPrefix =
    options?.tableName && fieldName
      ? localizationPrefixes[options.tableName]?.[fieldName]
      : undefined;
  let toString = (value: any) =>
    localizationPrefix !== undefined
      ? i18next.t(localizationPrefix + value, value)
      : value;

  if (localizationPrefix === undefined) {
    const canTranslateDataBlockDictionary =
        !!options?.tableName && dataBlockAuditTables.has(options.tableName);
    const canTranslateCommonAuditKey =
        canTranslateDataBlockDictionary || options?.tableName === 'TenantSetting';

    toString = (value) =>
        translateAuditValue(value, {
          dataBlockDictionary: canTranslateDataBlockDictionary,
          commonAuditKey: canTranslateCommonAuditKey,
        });
  }

  if (isValueEmpty(value)) return "''";
  if (Array.isArray(value)) {
    if (typeof value[0] === 'object') {
      // this is Table
      const stringTable = value
        .map((row) => {
          return (
            '- ' +
            Object.keys(row)
              .filter((key) => !key.startsWith('__'))
              .map((key) => `${key}: ${toString(row[key])}`)
              .join(', ')
          );
        })
        .join('\n');
      return '\n' + stringTable + '\n';
    }
    return value.map(toString).join(', ');
  }

  if (value instanceof String) {
    return `'${toString(value)}'`;
  }
  if (typeof value === 'string') {
    return toString(value);
  }
  return value;
}

function translateAuditValue(
    value: any,
    options: { dataBlockDictionary: boolean; commonAuditKey: boolean },
) {
  if (typeof value !== 'string') return value;

  if (options.dataBlockDictionary && value.startsWith('dictionaries')) {
    return i18next.t(dataBlockFieldNs + value, value);
  }

  if (
      options.commonAuditKey &&
      (value.startsWith('visit_type') ||
          value.startsWith('calendar_configuration'))
  ) {
    return i18next.t(value as any);
  }

  return value;
}

function flattenCustomFields(values: AuditRecordValues) {
  let result = values;
  const customFieldsObject = values[customFields];
  if (customFieldsObject) {
    result = Object.assign({}, values, customFieldsObject);
    delete result[customFields];
  }
  return result;
}

function filterEmptyValues(changes: AuditRecordValues) {
  const result: { [key: string]: any } = {};
  Object.keys(changes).forEach((field) => {
    const value = changes[field];
    if (!isValueEmpty(value) && field !== customFields) {
      result[field] = value;
    }
  });
  return result;
}

function isValueEmpty(value: any) {
  return value === null || value === undefined || isEmpty(value.toString());
}

type AuditRecordValues = { [key: string]: any | undefined };
